import React, { useState, useEffect, useRef } from 'react';
import LaunchBanner from '../../components/LaunchBanner';

/**
 * SeniorLivingPartnership — /senior-living
 *
 * Positioned as DIGITAL SPIRITUAL CARE AND COMPANIONSHIP, not a wellness app.
 * Two audiences on one page:
 *   1. Senior living operators (nonprofit and for-profit)
 *   2. Nonprofit grant partners who apply for funding with FHO named as the
 *      technology partner
 *
 * Deliberate departures from the other partnership pages:
 *   - No QuoteTool. Senior living has two payment models (bundled into the
 *     resident rate, or sponsored seats) and no validated demand yet. The CTA
 *     is a conversation, not a price.
 *   - No emoji iconography. This audience is licensed operators and grant
 *     committees; typography and colour carry the hierarchy instead.
 */

const SeniorLivingPartnership = () => {
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionRefs = useRef({});

  const [form, setForm] = useState({
    orgName: '', contactName: '', title: '', email: '', count: '', settingType: 'independent'
  });
  const [status, setStatus] = useState('idle');   // idle | loading | success | error
  const [errMsg, setErrMsg] = useState('');
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const SETTINGS = [
    ['independent', 'Independent living'],
    ['assisted', 'Assisted living'],
    ['ccrc', 'Life plan / continuing care community'],
    ['memory', 'Memory care'],
    ['affordable', 'Affordable senior housing'],
    ['home', 'Home and community-based services'],
    ['association', 'Association or provider network'],
    ['foundation', 'Foundation or funder']
  ];

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.orgName.trim() || !form.contactName.trim() || !form.email.includes('@')) {
      setErrMsg('Organization, name, and a valid email are needed.');
      setStatus('error'); return;
    }
    setStatus('loading'); setErrMsg('');
    const settingLabel = (SETTINGS.find(([v]) => v === form.settingType) || [])[1] || 'Senior living';
    try {
      const res = await fetch(window.location.origin + '/api/v1/quote-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orgName: form.orgName.trim(),
          contactName: form.contactName.trim(),
          title: form.title.trim() || 'Not given',
          email: form.email.trim(),
          count: String(parseInt(form.count, 10) || 0),   // int(count) is called server-side
          partnershipType: 'ltc',
          segmentLabel: `Senior living \u2014 ${settingLabel}`,
          quote: {
            type: 'inquiry',
            tier: 'Spiritual care layer \u2014 conversation requested',
            price: 'To be discussed',
            monthly: null
          }
        })
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      setStatus('success');
    } catch (e) {
      setErrMsg('Something went wrong. Please email partnerships@freehappyone.com.');
      setStatus('error');
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );
    Object.values(sectionRefs.current).forEach((ref) => { if (ref) observer.observe(ref); });
    return () => observer.disconnect();
  }, []);

  const setSectionRef = (id) => (el) => { sectionRefs.current[id] = el; };
  const isVisible = (id) => visibleSections.has(id);
  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const fadeIn = (id, delay = 0) => ({
    opacity: isVisible(id) ? 1 : 0,
    transform: isVisible(id) ? 'translateY(0)' : 'translateY(28px)',
    transition: `all 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`
  });

  const T = {
    navy: '#1e3a5f',
    deep: '#14283f',
    ink: '#243a56',
    mute: '#5b7392',
    gold: '#c8922f',
    sand: '#faf7f1',
    pageBg: '#f7f9fc',
    line: '#e2e9f2',
    white: '#ffffff',
    serif: "Georgia, 'Times New Roman', serif",
    sans: "'Segoe UI', system-ui, -apple-system, sans-serif"
  };

  const wrap = { maxWidth: '1080px', margin: '0 auto', padding: '0 28px' };
  const eyebrow = {
    fontSize: '11px', fontWeight: 700, letterSpacing: '2.5px',
    textTransform: 'uppercase', color: T.gold, marginBottom: '14px'
  };
  const h2 = {
    fontFamily: T.serif, fontSize: 'clamp(26px, 3.4vw, 36px)', fontWeight: 700,
    color: T.navy, lineHeight: 1.18, margin: '0 0 18px'
  };
  const lead = {
    fontFamily: T.sans, fontSize: '17px', lineHeight: 1.65, color: T.ink, margin: '0 0 16px'
  };
  const body = {
    fontFamily: T.sans, fontSize: '15px', lineHeight: 1.62, color: T.mute, margin: 0
  };
  const card = {
    background: T.white, border: `1px solid ${T.line}`, borderRadius: '14px',
    padding: '28px 30px', boxShadow: '0 1px 3px rgba(20,40,63,0.05)'
  };
  const section = { padding: '84px 0' };
  const label = { display: 'block', fontFamily: T.sans, fontSize: '12.5px', fontWeight: 700, color: T.navy, marginBottom: '6px', marginTop: '16px' };
  const input = { width: '100%', padding: '12px 14px', borderRadius: '9px', border: '1px solid #cbd6e6', fontFamily: T.sans, fontSize: '15px', color: T.ink, boxSizing: 'border-box', background: '#fff' };

  const coaches = [
    { name: 'Emit', line: 'Scripture and spiritual companionship',
      desc: 'Bible study, prayer, and reflection for residents who want their faith engaged — on a Tuesday afternoon or at four in the morning.',
      color: '#4f46e5' },
    { name: 'SAFE', line: 'A steady presence in a hard hour',
      desc: 'For grief, anxiety, and the nights that feel long. Faith-grounded, unhurried, and it knows when a person needs a person.',
      color: '#059669' },
    { name: 'Family Coach', line: 'The relationships that keep going',
      desc: 'Adult children, grandchildren, old griefs and old joys. The relational life of a resident does not end at the door.',
      color: '#c8922f' },
    { name: 'CHAMP', line: 'Purpose after the working life',
      desc: 'For residents who still want direction, discipline, and something to aim at. Secular, and available to anyone.',
      color: '#b45309' }
  ];

  const escalation = [
    { n: '01', t: 'A resident opens up', d: 'At an hour when nobody is scheduled to be there. About a diagnosis, a spouse, a fear they have not said out loud.' },
    { n: '02', t: 'It stays with them', d: 'Faith-grounded, never clinical. It listens before it offers anything, and it does not rush them toward resolution.' },
    { n: '03', t: 'It offers a person', d: 'A chaplain, a staff member, or a family member — warmly, as an offer rather than a redirect.' },
    { n: '04', t: 'Only with consent', d: 'A short summary travels with the request, and only if the resident ticks the box. The default is no, every time.' }
  ];

  const notThis = [
    ['It is not clinical care', 'It does not diagnose, treat, or manage a medical condition, and it makes no claim to. Spiritual care has lived inside licensed settings for a century without being treatment.'],
    ['It is not a call system', 'It is not a nurse call, an alert, or a monitoring tool. It is not part of your safety infrastructure and should never be relied on as such.'],
    ['It does not replace your staff', 'Chaplains, social workers, and life enrichment teams are the care. This carries the hours nobody can staff, and hands people back.'],
    ['It is not an advocacy tool', 'If a resident raises a concern about their care or their family, it does not counsel them through it. It recognises what it heard and points to someone who can act.']
  ];

  const trust = [
    ['Retention', 'Conversation content is retained ninety days on our infrastructure, then permanently deleted. Enforced by a scheduled process, not by policy alone.'],
    ['No model training', 'Our AI providers operate under commercial terms: retention up to thirty days for abuse monitoring, and resident conversations are never used to train models.'],
    ['Consent-gated sharing', 'A chaplain or family member sees a summary only when the resident asks. Community-level insight is aggregated and anonymised.'],
    ['Access', 'No anonymous entry. Residents arrive through their community, the same way your existing systems already gate access.']
  ];

  return (
    <div style={{ background: T.pageBg, minHeight: '100vh', fontFamily: T.sans }}>
      <LaunchBanner />

      {/* ============ NAV ============ */}
      <div style={{ background: T.deep, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ ...wrap, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 28px' }}>
          <a href="/" style={{ fontFamily: T.serif, fontSize: '18px', fontWeight: 700, color: '#fff', textDecoration: 'none' }}>
            <span style={{ color: '#8b93f0' }}>F</span><span style={{ color: '#f87171' }}>H</span><span style={{ color: '#34d399' }}>O</span> Platform
          </a>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <a href="/" style={{ fontSize: '13.5px', color: '#c9d6ea', textDecoration: 'none', padding: '8px 14px' }}>Explore the platform</a>
            <button onClick={() => scrollTo('talk')} style={{
              padding: '9px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer',
              background: T.gold, color: '#1a1508', fontFamily: T.sans, fontSize: '13.5px', fontWeight: 700
            }}>Start a conversation</button>
          </div>
        </div>
      </div>

      {/* ============ HERO ============ */}
      <div style={{ background: `linear-gradient(168deg, ${T.deep} 0%, ${T.navy} 62%, #2d4a7a 100%)` }}>
        <div style={{ ...wrap, padding: '96px 28px 88px' }}>
          <div style={{ ...eyebrow, color: '#e0b45c' }}>For senior living communities</div>
          <h1 style={{
            fontFamily: T.serif, fontSize: 'clamp(34px, 5.2vw, 52px)', fontWeight: 700,
            color: T.white, lineHeight: 1.12, margin: '0 0 22px', maxWidth: '780px'
          }}>
            Someone to talk with, at the hour it is needed.
          </h1>
          <p style={{
            fontFamily: T.sans, fontSize: 'clamp(17px, 2.1vw, 20px)', lineHeight: 1.6,
            color: '#c9d6ea', margin: '0 0 34px', maxWidth: '660px'
          }}>
            Digital spiritual care and companionship for residents — in the evenings, on the
            weekends, and in the long stretch between a chaplain&rsquo;s visit and the next one.
            Built to hand people back to the humans who care for them.
          </p>
          <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
            <button onClick={() => scrollTo('how')} style={{
              padding: '15px 32px', borderRadius: '10px', border: 'none', cursor: 'pointer',
              background: T.gold, color: '#1a1508', fontFamily: T.sans, fontSize: '15px', fontWeight: 700
            }}>How it works</button>
            <a href="/" style={{
              padding: '15px 32px', borderRadius: '10px', display: 'inline-block',
              background: 'transparent', color: T.white, border: '1.5px solid rgba(255,255,255,0.4)',
              fontFamily: T.sans, fontSize: '15px', fontWeight: 700, textDecoration: 'none'
            }}>See the platform</a>
          </div>
        </div>
      </div>

      {/* ============ THE PROBLEM ============ */}
      <div id="problem" ref={setSectionRef('problem')} style={{ ...section, background: T.white }}>
        <div style={{ ...wrap, ...fadeIn('problem') }}>
          <div style={eyebrow}>The problem your programming cannot fully solve</div>
          <h2 style={h2}>A resident can be surrounded by people and still be alone.</h2>
          <p style={{ ...lead, maxWidth: '760px' }}>
            Activities end. Visitors go home. The chaplain comes on Tuesday. And the hours that
            are hardest — the evening, the sleepless night, the morning after bad news — are
            almost never the hours anyone is scheduled to be present.
          </p>
          <div style={{
            ...card, background: T.sand, borderLeft: `4px solid ${T.gold}`,
            marginTop: '32px', maxWidth: '860px'
          }}>
            <p style={{ ...lead, fontStyle: 'italic', margin: '0 0 14px' }}>
              &ldquo;Socialization promotes a sense of purpose and connection with others. It
              significantly improves the mental health, cognitive function, and physical health
              of seniors.&rdquo;
            </p>
            <p style={{ ...body, fontSize: '13.5px' }}>
              James Balda, President &amp; CEO, Argentum — testimony before the U.S. Senate
              Special Committee on Aging, March 2025
            </p>
          </div>
          <p style={{ ...lead, marginTop: '30px', maxWidth: '760px' }}>
            The sector already knows connection is the need. What has been missing is something
            that can be present in the hours between the people.
          </p>
        </div>
      </div>

      {/* ============ WHAT IT IS ============ */}
      <div id="what" ref={setSectionRef('what')} style={{ ...section, background: T.pageBg }}>
        <div style={{ ...wrap, ...fadeIn('what') }}>
          <div style={eyebrow}>What it is</div>
          <h2 style={h2}>Spiritual care, in a category you already understand.</h2>
          <p style={{ ...lead, maxWidth: '820px' }}>
            Chaplaincy has existed inside licensed care settings for a century without being
            clinical. It is staffed, budgeted, and nobody confuses it with treatment. This sits
            in that same category — companionship and spiritual presence, delivered digitally,
            with a designed path back to a human being.
          </p>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(248px, 1fr))',
            gap: '18px', marginTop: '38px'
          }}>
            {coaches.map((c, i) => (
              <div key={c.name} style={{ ...card, ...fadeIn('what', 0.08 * i), padding: '26px 26px 28px' }}>
                <div style={{ width: '34px', height: '3px', background: c.color, borderRadius: '2px', marginBottom: '16px' }} />
                <div style={{ fontFamily: T.serif, fontSize: '21px', fontWeight: 700, color: T.navy }}>{c.name}</div>
                <div style={{
                  fontSize: '11px', fontWeight: 700, letterSpacing: '1.4px', textTransform: 'uppercase',
                  color: c.color, margin: '7px 0 13px'
                }}>{c.line}</div>
                <p style={body}>{c.desc}</p>
              </div>
            ))}
          </div>
          <p style={{ ...body, marginTop: '24px', fontSize: '14px' }}>
            Communities choose which coaches their residents can reach. A community that wants
            only secular companionship can have exactly that.
          </p>
        </div>
      </div>

      {/* ============ HOW ESCALATION WORKS ============ */}
      <div id="how" ref={setSectionRef('how')} style={{ ...section, background: T.white }}>
        <div style={{ ...wrap, ...fadeIn('how') }}>
          <div style={eyebrow}>How it works</div>
          <h2 style={h2}>The point is not the conversation. It is what the conversation leads to.</h2>
          <p style={{ ...lead, maxWidth: '780px' }}>
            Success here is measured by chaplain visits requested and family calls made — not by
            time spent in an app. The software&rsquo;s job is to be there at three in the morning
            and then get out of the way.
          </p>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
            gap: '18px', marginTop: '38px'
          }}>
            {escalation.map((s, i) => (
              <div key={s.n} style={{
                ...card, ...fadeIn('how', 0.08 * i),
                background: i === 3 ? T.sand : T.white,
                border: i === 3 ? `1px solid ${T.gold}` : `1px solid ${T.line}`
              }}>
                <div style={{
                  fontFamily: T.serif, fontSize: '30px', fontWeight: 700,
                  color: i === 3 ? T.gold : '#c3d0e2', lineHeight: 1, marginBottom: '14px'
                }}>{s.n}</div>
                <div style={{ fontFamily: T.serif, fontSize: '17px', fontWeight: 700, color: T.navy, marginBottom: '9px' }}>{s.t}</div>
                <p style={body}>{s.d}</p>
              </div>
            ))}
          </div>
          <div style={{ ...card, background: T.navy, marginTop: '26px', border: 'none' }}>
            <p style={{
              fontFamily: T.serif, fontSize: '20px', fontWeight: 700, color: T.white,
              margin: '0 0 10px', lineHeight: 1.35
            }}>
              Nothing a resident says reaches anyone unless the resident chooses to share it.
            </p>
            <p style={{ ...body, color: '#c9d6ea' }}>
              Consent is captured per request and is never a stored preference. A resident who
              wants to see their daughter can ask for that — and never has to tell the story twice.
            </p>
          </div>
        </div>
      </div>

      {/* ============ WHAT IT IS NOT ============ */}
      <div id="not" ref={setSectionRef('not')} style={{ ...section, background: T.pageBg }}>
        <div style={{ ...wrap, ...fadeIn('not') }}>
          <div style={eyebrow}>Being clear about the boundaries</div>
          <h2 style={h2}>Four things this is not.</h2>
          <p style={{ ...lead, maxWidth: '760px' }}>
            We would rather tell you now than be asked in a compliance review.
          </p>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '18px', marginTop: '34px'
          }}>
            {notThis.map(([t, d], i) => (
              <div key={t} style={{ ...card, ...fadeIn('not', 0.07 * i) }}>
                <div style={{ fontFamily: T.serif, fontSize: '18px', fontWeight: 700, color: T.navy, marginBottom: '10px' }}>{t}</div>
                <p style={body}>{d}</p>
              </div>
            ))}
          </div>
          <div style={{ ...card, marginTop: '22px', borderLeft: `4px solid ${T.navy}` }}>
            <p style={{ ...lead, margin: 0 }}>
              When a resident describes something that needs a person — a concern about their
              care, a family situation that has gone wrong, a weight beyond what companionship
              can hold — it recognises that, stops, and points to someone who can act. Knowing
              where it ends is a designed feature, not an omission.
            </p>
          </div>
        </div>
      </div>

      {/* ============ TRUST ============ */}
      <div id="trust" ref={setSectionRef('trust')} style={{ ...section, background: T.white }}>
        <div style={{ ...wrap, ...fadeIn('trust') }}>
          <div style={eyebrow}>Trust</div>
          <h2 style={h2}>Where resident conversations go.</h2>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '18px', marginTop: '32px'
          }}>
            {trust.map(([t, d], i) => (
              <div key={t} style={{ ...card, ...fadeIn('trust', 0.07 * i) }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '11px', marginBottom: '11px' }}>
                  <div style={{ width: '9px', height: '9px', borderRadius: '50%', background: T.gold }} />
                  <div style={{ fontFamily: T.serif, fontSize: '18px', fontWeight: 700, color: T.navy }}>{t}</div>
                </div>
                <p style={body}>{d}</p>
              </div>
            ))}
          </div>
          <p style={{ ...body, marginTop: '22px', fontStyle: 'italic', fontSize: '14px' }}>
            In a pilot, the retention window is something we set with you — not something you inherit.
          </p>
        </div>
      </div>

      {/* ============ TWO WAYS TO FUND IT ============ */}
      <div id="fund" ref={setSectionRef('fund')} style={{ ...section, background: T.sand }}>
        <div style={{ ...wrap, ...fadeIn('fund') }}>
          <div style={eyebrow}>Two ways communities bring this in</div>
          <h2 style={h2}>Operators fund it directly. Or a grant does.</h2>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
            gap: '20px', marginTop: '34px'
          }}>
            <div style={{ ...card, ...fadeIn('fund', 0.05) }}>
              <div style={{ fontFamily: T.serif, fontSize: '21px', fontWeight: 700, color: T.navy, marginBottom: '12px' }}>
                As part of the community
              </div>
              <p style={{ ...body, marginBottom: '14px' }}>
                Offered as a resident amenity and carried on the community&rsquo;s own budget, or
                included in the resident rate the way other services are. Some communities also
                sponsor seats for residents who could not otherwise afford one.
              </p>
              <p style={{ ...body, fontSize: '13.5px', fontStyle: 'italic' }}>
                Scope and pricing follow your model. We would rather understand how you structure
                resident services than propose a number into a vacuum.
              </p>
            </div>
            <div style={{ ...card, ...fadeIn('fund', 0.12), borderTop: `4px solid ${T.gold}` }}>
              <div style={{ fontFamily: T.serif, fontSize: '21px', fontWeight: 700, color: T.navy, marginBottom: '12px' }}>
                As a funded pilot
              </div>
              <p style={{ ...body, marginBottom: '14px' }}>
                Social connection among older adults is actively funded right now — by the Humana
                Foundation, RRF Foundation for Aging, the John A. Hartford Foundation, and a
                growing number of state programmes.
              </p>
              <p style={{ ...body }}>
                Nonprofit communities and aging-services organisations are eligible applicants. We
                serve as the named technology partner, help build the proposal, and stand up the
                pilot. You hold the grant and the programme.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ============ PILOT ============ */}
      <div id="pilot" ref={setSectionRef('pilot')} style={{ ...section, background: T.white }}>
        <div style={{ ...wrap, ...fadeIn('pilot') }}>
          <div style={eyebrow}>If it made sense to try</div>
          <h2 style={h2}>What a pilot looks like.</h2>
          <div style={{ marginTop: '30px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {[
              ['One community, or a few', 'Small enough to watch closely. Independent living is the natural starting point, where resident autonomy is not in question.'],
              ['Ninety days', 'Long enough to see whether residents come back, and whether requests actually reach chaplains and families.'],
              ['Co-branded', 'Your name forward. Partner branding is configuration on our side, not development.'],
              ['Measured on your metric', 'Visits requested. Family contact made. Resident-reported connection. Not screen time.']
            ].map(([t, d], i) => (
              <div key={t} style={{
                ...fadeIn('pilot', 0.06 * i),
                display: 'flex', flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '10px' : '24px', alignItems: 'flex-start',
                padding: '22px 4px', borderBottom: `1px solid ${T.line}`
              }}>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'baseline', minWidth: isMobile ? 'auto' : '230px' }}>
                  <span style={{ fontFamily: T.serif, fontSize: '15px', fontWeight: 700, color: T.gold }}>{`0${i + 1}`}</span>
                  <span style={{ fontFamily: T.serif, fontSize: '18px', fontWeight: 700, color: T.navy }}>{t}</span>
                </div>
                <p style={{ ...body, flex: isMobile ? 'none' : 1, paddingLeft: isMobile ? '34px' : 0 }}>{d}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ============ CTA + LEAD CAPTURE ============ */}
      <div id="talk" ref={setSectionRef('talk')} style={{ background: T.deep, padding: '80px 0' }}>
        <div style={{ ...wrap, ...fadeIn('talk') }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '44px', alignItems: 'start' }}>
            <div>
              <div style={{ ...eyebrow, color: '#e0b45c' }}>What we are asking for</div>
              <h2 style={{ ...h2, color: T.white }}>Not a purchase. A conversation about your residents.</h2>
              <p style={{ fontFamily: T.sans, fontSize: '17px', lineHeight: 1.65, color: '#c9d6ea', margin: '0 0 20px' }}>
                We would want your chaplains, social workers and life enrichment team reading real
                transcripts and telling us where the line sits &mdash; before a single resident sees it.
              </p>
              <p style={{ fontFamily: T.sans, fontSize: '14px', lineHeight: 1.6, color: '#8fa5c4' }}>
                Prefer email? <span style={{ color: '#e0b45c' }}>partnerships@freehappyone.com</span>
              </p>
              <a href="/" style={{ display: 'inline-block', marginTop: '18px', fontFamily: T.sans, fontSize: '14px', color: '#c9d6ea' }}>
                Or explore the platform first &rarr;
              </a>
            </div>

            <div style={{ background: T.white, borderRadius: '14px', padding: '30px 32px' }}>
              {status === 'success' ? (
                <div style={{ textAlign: 'center', padding: '26px 0' }}>
                  <div style={{ fontFamily: T.serif, fontSize: '22px', fontWeight: 700, color: T.navy, marginBottom: '10px' }}>Thank you.</div>
                  <p style={body}>
                    We read every one of these personally and will follow up. For anything urgent,
                    write to partnerships@freehappyone.com.
                  </p>
                </div>
              ) : (
                <>
                  <div style={{ fontFamily: T.serif, fontSize: '20px', fontWeight: 700, color: T.navy, marginBottom: '4px' }}>Tell us about your community</div>
                  <p style={{ ...body, fontSize: '13.5px' }}>No obligation, and no sales sequence.</p>

                  <label style={label}>Organization *</label>
                  <input style={input} value={form.orgName} onChange={set('orgName')} placeholder="Community or organization name" />

                  <label style={label}>Your name *</label>
                  <input style={input} value={form.contactName} onChange={set('contactName')} placeholder="First and last" />

                  <label style={label}>Role</label>
                  <input style={input} value={form.title} onChange={set('title')} placeholder="Executive Director, VP of Mission, Life Enrichment…" />

                  <label style={label}>Email *</label>
                  <input style={input} type="email" value={form.email} onChange={set('email')} placeholder="you@organization.org" />

                  <label style={label}>Setting</label>
                  <select style={input} value={form.settingType} onChange={set('settingType')}>
                    {SETTINGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>

                  <label style={label}>Residents served (approximate)</label>
                  <input style={input} type="number" min="0" value={form.count} onChange={set('count')} placeholder="e.g. 180" />

                  <button onClick={submit} disabled={status === 'loading'} style={{
                    marginTop: '22px', width: '100%', padding: '14px', borderRadius: '10px', border: 'none',
                    cursor: status === 'loading' ? 'wait' : 'pointer', background: T.navy, color: T.white,
                    fontFamily: T.sans, fontSize: '15.5px', fontWeight: 700
                  }}>
                    {status === 'loading' ? 'Sending…' : 'Start the conversation'}
                  </button>

                  {status === 'error' && (
                    <p style={{ ...body, color: '#b91c1c', marginTop: '12px', fontSize: '13.5px' }}>{errMsg}</p>
                  )}
                  <p style={{ ...body, fontSize: '12.5px', marginTop: '14px' }}>
                    We review every enquiry personally. Your details are never shared or sold.
                  </p>
                </>
              )}
            </div>
          </div>

          <p style={{
            fontFamily: T.sans, fontSize: '13px', color: '#8296b5', marginTop: '40px',
            paddingTop: '22px', borderTop: '1px solid rgba(255,255,255,0.12)'
          }}>
            FHO Platform &nbsp;&middot;&nbsp; MindStreams Technology, Thornbee LLC &nbsp;&middot;&nbsp; freehappyone.com
          </p>
        </div>
      </div>
    </div>
  );
};

export default SeniorLivingPartnership;
